<script setup>
import { ref, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import inboxesAPI from 'dashboard/api/inboxes';

const router = useRouter();
const { accountId } = useAccount();

// ── State ──────────────────────────────────────────────────────────────────
const inboxName = ref('');
const nodeServiceUrl = ref('http://localhost:3001');
const creating = ref(false);
const createdInboxId = ref(null);
const qrDataUrl = ref(null);
const status = ref('idle'); // idle | creating | polling | connected | error
const pollingInterval = ref(null);

// ── Helpers ────────────────────────────────────────────────────────────────
async function createInbox() {
  if (!inboxName.value.trim()) {
    useAlert('Please enter an inbox name');
    return;
  }

  creating.value = true;
  status.value = 'creating';

  try {
    const { data } = await inboxesAPI.create({
      name: inboxName.value.trim(),
      channel: {
        type: 'whatsapp_nodejs',
        node_service_url: nodeServiceUrl.value.trim() || 'http://localhost:3001',
      },
    });

    createdInboxId.value = data.id;
    status.value = 'polling';
    startPolling(data.id);
  } catch (error) {
    status.value = 'error';
    useAlert(error?.response?.data?.message || 'Failed to create inbox');
  } finally {
    creating.value = false;
  }
}

function startPolling(inboxId) {
  qrDataUrl.value = null;
  pollingInterval.value = setInterval(() => fetchQR(inboxId), 3000);
  fetchQR(inboxId);
}

async function fetchQR(inboxId) {
  try {
    const { data } = await inboxesAPI.getWhatsappNodejsQR(inboxId);
    if (data?.qr) {
      qrDataUrl.value = data.qr;
    }
    if (data?.status === 'connected') {
      stopPolling();
      status.value = 'connected';
    }
  } catch (_) {
    // keep polling silently
  }
}

function stopPolling() {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value);
    pollingInterval.value = null;
  }
}

function goToAgents() {
  router.push({
    name: 'settings_inboxes_add_agents',
    params: { inbox_id: createdInboxId.value },
  });
}

onBeforeUnmount(() => stopPolling());
</script>

<template>
  <div class="max-w-lg mx-auto py-8">
    <!-- Step 1: Configure inbox -->
    <div v-if="status === 'idle' || status === 'creating'">
      <h2 class="text-xl font-semibold mb-1">
        WhatsApp Personal (QR Code)
      </h2>
      <p class="text-sm text-n-500 dark:text-n-400 mb-6">
        Connect a personal WhatsApp number by scanning a QR code. Supports private chats and groups.
      </p>

      <div class="flex flex-col gap-4">
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium">Inbox Name</span>
          <input
            v-model="inboxName"
            type="text"
            placeholder="My WhatsApp Account"
            class="input"
          />
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium">Node.js Service URL</span>
          <input
            v-model="nodeServiceUrl"
            type="text"
            placeholder="http://localhost:3001"
            class="input"
          />
          <p class="text-xs text-n-500">URL where the whatsapp-node-service is running</p>
        </label>

        <button
          class="button success"
          :disabled="creating"
          @click="createInbox"
        >
          <span v-if="creating">Creating...</span>
          <span v-else>Create Inbox &amp; Generate QR</span>
        </button>
      </div>
    </div>

    <!-- Step 2: QR code polling -->
    <div v-else-if="status === 'polling'" class="flex flex-col items-center gap-6">
      <h2 class="text-xl font-semibold">Scan QR Code with WhatsApp</h2>
      <p class="text-sm text-n-500 text-center">
        Open WhatsApp on your phone → <strong>Settings → Linked Devices → Link a Device</strong>
      </p>

      <div
        v-if="qrDataUrl"
        class="border border-n-200 dark:border-n-700 rounded-xl p-4 bg-white"
      >
        <img :src="qrDataUrl" alt="WhatsApp QR Code" class="w-64 h-64" />
      </div>

      <div v-else class="flex flex-col items-center gap-2 text-n-500">
        <span class="i-lucide-loader-circle animate-spin text-3xl" />
        <p class="text-sm">Generating QR code...</p>
      </div>

      <p class="text-xs text-n-400">QR code refreshes automatically every 60 seconds</p>
    </div>

    <!-- Step 3: Connected -->
    <div v-else-if="status === 'connected'" class="flex flex-col items-center gap-6">
      <span class="i-lucide-check-circle text-5xl text-green-500" />
      <h2 class="text-xl font-semibold">WhatsApp Connected!</h2>
      <p class="text-sm text-n-500 text-center">
        Your WhatsApp account is now linked. Messages will appear in the inbox.
      </p>
      <button class="button success" @click="goToAgents">
        Add Agents &amp; Finish
      </button>
    </div>

    <!-- Error -->
    <div v-else-if="status === 'error'" class="flex flex-col items-center gap-4">
      <span class="i-lucide-alert-circle text-4xl text-red-500" />
      <h2 class="text-lg font-semibold">Something went wrong</h2>
      <button class="button" @click="status = 'idle'">Try Again</button>
    </div>
  </div>
</template>
